/**
 * Language Switch Hook
 *
 * Provides language switching functionality with:
 * - switchLanguage() function to change the current language
 * - localStorage persistence for language preference
 * - HTML lang attribute updates for accessibility
 * - RTL direction support for RTL languages
 *
 * @module i18n/useLanguageSwitch
 * @see Requirements 2.2, 2.3, 2.4, 12.1-12.5
 */

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { updateDocumentDirection, isRTL } from './rtl-support';

/**
 * Supported language configuration.
 */
export interface LanguageConfig {
  /** ISO language code (e.g., 'en', 'zh-TW') */
  code: string;
  /** English name of the language */
  name: string;
  /** Native name of the language */
  nativeName: string;
}

/**
 * List of supported languages.
 * Add new languages here when expanding i18n support.
 */
export const SUPPORTED_LANGUAGES: readonly LanguageConfig[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'zh-TW', name: 'Traditional Chinese', nativeName: '繁體中文' },
] as const;

/** localStorage key for language preference (matches i18next config) */
const LANGUAGE_STORAGE_KEY = 'i18nextLng';

/**
 * Return type for useLanguageSwitch hook.
 */
export interface UseLanguageSwitchResult {
  /** Current active language code */
  currentLanguage: string;
  /** List of all supported languages */
  supportedLanguages: readonly LanguageConfig[];
  /** Switch to a new language */
  switchLanguage: (languageCode: string) => Promise<void>;
  /** Check if a language code is supported */
  isLanguageSupported: (languageCode: string) => boolean;
  /** Check if current language is RTL */
  isCurrentLanguageRTL: boolean;
}

/**
 * Hook for managing language switching.
 *
 * Handles:
 * - Switching the i18n language
 * - Persisting preference to localStorage
 * - Updating the HTML lang attribute
 *
 * @example
 * ```tsx
 * function LanguageSelector() {
 *   const { currentLanguage, supportedLanguages, switchLanguage } = useLanguageSwitch();
 *
 *   return (
 *     <select
 *       value={currentLanguage}
 *       onChange={(e) => switchLanguage(e.target.value)}
 *     >
 *       {supportedLanguages.map((lang) => (
 *         <option key={lang.code} value={lang.code}>
 *           {lang.nativeName}
 *         </option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 *
 * @returns Object containing language state and switch function
 */
export function useLanguageSwitch(): UseLanguageSwitchResult {
  const { i18n } = useTranslation();

  /**
   * Check if a language code is in the supported list.
   */
  const isLanguageSupported = useCallback((languageCode: string): boolean => {
    return SUPPORTED_LANGUAGES.some((lang) => lang.code === languageCode);
  }, []);

  /**
   * Switch to a new language.
   * Updates i18n, localStorage, HTML lang attribute, and RTL direction.
   */
  const switchLanguage = useCallback(
    async (languageCode: string): Promise<void> => {
      // Validate language is supported
      if (!isLanguageSupported(languageCode)) {
        console.warn(
          `Language "${languageCode}" is not supported. Supported languages: ${SUPPORTED_LANGUAGES.map((l) => l.code).join(', ')}`
        );
        return;
      }

      // Change i18n language
      await i18n.changeLanguage(languageCode);

      // Persist to localStorage
      localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);

      // Update HTML lang attribute for accessibility
      document.documentElement.lang = languageCode;

      // Update RTL direction and language-specific classes (Task 13)
      updateDocumentDirection(languageCode);
    },
    [i18n, isLanguageSupported]
  );

  /**
   * Memoized list of supported languages.
   */
  const supportedLanguages = useMemo(() => SUPPORTED_LANGUAGES, []);

  /**
   * Check if current language is RTL.
   */
  const isCurrentLanguageRTL = useMemo(() => isRTL(i18n.language), [i18n.language]);

  return {
    currentLanguage: i18n.language,
    supportedLanguages,
    switchLanguage,
    isLanguageSupported,
    isCurrentLanguageRTL,
  };
}
